"use client";

import { useState, useEffect } from "react";
import { Pencil } from "lucide-react";
import { getFooter, updateFooter } from "../../services/api";
import { toast } from "react-toastify";
import { Button } from "../../components/ui/button";
import { Card, CardContent } from "../../components/ui/card";
import { Input } from "../../components/ui/input";
import { Textarea } from "../../components/ui/textarea";

// Types for our footer data
export interface FooterData {
  id: string;
  text: string;
  email: string;
  phone_number: string;
  address: string;
  facebook_link: string;
  youtube_link: string;
  instagram_link: string;
  twitter_link: string;
  tiktok_link: string;
  created_at: string;
  updated_at: string;
}

export default function FooterEditor() {
  const initialFooterData: FooterData = {
    id: "",
    text: "",
    email: "",
    phone_number: "",
    address: "",
    facebook_link: "",
    youtube_link: "",
    instagram_link: "",
    twitter_link: "",
    tiktok_link: "",
    created_at: "",
    updated_at: "",
  };

  const [footerData, setFooterData] = useState<FooterData>(initialFooterData);
  const [isEditing, setIsEditing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Load data from API on component mount
  useEffect(() => {
    const fetchFooterData = async () => {
      try {
        setIsLoading(true);
        const response = await getFooter();

        if (response.data && response.data.data.length > 0) {
          // Use the first footer in the array
          setFooterData(response.data.data[0]);
        } else {
          // Initialize with empty data if no footer exists
          setFooterData(initialFooterData);
        }
        setError(null);
      } catch (err) {
        setError("Failed to load footer data");
        console.error(err);
      } finally {
        setIsLoading(false);
      }
    };

    fetchFooterData();
  }, []);

  // Handle field changes
  const handleFieldChange = (field: keyof FooterData, value: string) => {
    setFooterData({
      ...footerData,
      [field]: value,
    });
  };

  // Save changes to the API
  const saveChanges = async () => {
    try {
      setIsSaving(true);

      // Prepare data for API
      const dataToSave: any = {
        text: footerData.text,
        email: footerData.email,
        phone_number: footerData.phone_number,
        address: footerData.address,
        facebook_link: footerData.facebook_link,
        youtube_link: footerData.youtube_link,
        instagram_link: footerData.instagram_link,
        twitter_link: footerData.twitter_link,
        tiktok_link: footerData.tiktok_link,
      };

      // Update existing footer
      if (footerData.id) {
        await updateFooter(footerData.id, dataToSave);
        toast.success("Footer updated successfully!");
      }

      setIsEditing(false);
      setError(null);
    } catch (err) {
      setError("Failed to save footer data");
      console.error(err);
    } finally {
      setIsSaving(false);
    }
  };

  if (error) {
    return (
      <div className="p-8">
        <div className="text-red-500 mb-4">{error}</div>
        <Button onClick={() => window.location.reload()}>Retry</Button>
      </div>
    );
  }

  return (
    <div className="p-8">
      <div className="flex justify-between items-center mb-8">
        <div>
          <h1 className="text-2xl font-serif font-medium">Footer Content</h1>
          <p className="text-sm text-muted-foreground">
            Modify footer content with precision.
          </p>
        </div>
        {!isEditing && (
          <Button
            variant="outline"
            className="border-[#B87775] text-[#B87775] hover:bg-rose-50"
            onClick={() => setIsEditing(true)}
          >
            <Pencil className="h-4 w-4 mr-2" />
            Edit
          </Button>
        )}
      </div>
      {isLoading ? (
        <div className="flex justify-center items-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900"></div>
        </div>
      ) : (
        <Card className="border-muted">
          <CardContent className="p-6 space-y-6">
            {/* Contact Information */}
            <div className="border rounded-lg p-6">
              <h2 className="text-lg font-medium mb-4">Contact Information</h2>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm mb-1">Email Address</label>
                  {isEditing ? (
                    <Input
                      type="email"
                      placeholder="Enter email address"
                      value={footerData.email}
                      onChange={(e) =>
                        handleFieldChange("email", e.target.value)
                      }
                    />
                  ) : (
                    <p className="p-2 border rounded-md bg-muted/20">
                      {footerData.email || "No email provided"}
                    </p>
                  )}
                </div>
                <div>
                  <label className="block text-sm mb-1">Phone Number</label>
                  {isEditing ? (
                    <Input
                      type="tel"
                      placeholder="Enter phone number"
                      value={footerData.phone_number}
                      onChange={(e) =>
                        handleFieldChange("phone_number", e.target.value)
                      }
                    />
                  ) : (
                    <p className="p-2 border rounded-md bg-muted/20">
                      {footerData.phone_number || "No phone provided"}
                    </p>
                  )}
                </div>
                <div>
                  <label className="block text-sm mb-1">Address</label>
                  {isEditing ? (
                    <Input
                      placeholder="Enter address"
                      value={footerData.address}
                      onChange={(e) =>
                        handleFieldChange("address", e.target.value)
                      }
                    />
                  ) : (
                    <p className="p-2 border rounded-md bg-muted/20">
                      {footerData.address || "No address provided"}
                    </p>
                  )}
                </div>
              </div>
            </div>

            {/* Social Links */}
            <div className="border rounded-lg p-6">
              <h2 className="text-lg font-medium mb-4">Social Links</h2>
              <p className="text-sm text-muted-foreground mb-4">
                (social media will be linked to the icons)
              </p>
              <div className="space-y-4">
                {/* Facebook */}
                <div>
                  <label className="block text-sm mb-1">Facebook</label>
                  {isEditing ? (
                    <Input
                      type="url"
                      placeholder="Enter Facebook URL"
                      value={footerData.facebook_link}
                      onChange={(e) =>
                        handleFieldChange("facebook_link", e.target.value)
                      }
                    />
                  ) : (
                    <p className="p-2 border rounded-md bg-muted/20">
                      {footerData.facebook_link || "No Facebook link provided"}
                    </p>
                  )}
                </div>

                {/* YouTube */}
                <div>
                  <label className="block text-sm mb-1">YouTube</label>
                  {isEditing ? (
                    <Input
                      type="url"
                      placeholder="Enter YouTube URL"
                      value={footerData.youtube_link}
                      onChange={(e) =>
                        handleFieldChange("youtube_link", e.target.value)
                      }
                    />
                  ) : (
                    <p className="p-2 border rounded-md bg-muted/20">
                      {footerData.youtube_link || "No YouTube link provided"}
                    </p>
                  )}
                </div>

                {/* Instagram */}
                <div>
                  <label className="block text-sm mb-1">Instagram</label>
                  {isEditing ? (
                    <Input
                      type="url"
                      placeholder="Enter Instagram URL"
                      value={footerData.instagram_link}
                      onChange={(e) =>
                        handleFieldChange("instagram_link", e.target.value)
                      }
                    />
                  ) : (
                    <p className="p-2 border rounded-md bg-muted/20">
                      {footerData.instagram_link ||
                        "No Instagram link provided"}
                    </p>
                  )}
                </div>

                {/* Twitter */}
                <div>
                  <label className="block text-sm mb-1">Twitter</label>
                  {isEditing ? (
                    <Input
                      type="url"
                      placeholder="Enter Twitter URL"
                      value={footerData.twitter_link}
                      onChange={(e) =>
                        handleFieldChange("twitter_link", e.target.value)
                      }
                    />
                  ) : (
                    <p className="p-2 border rounded-md bg-muted/20">
                      {footerData.twitter_link || "No Twitter link provided"}
                    </p>
                  )}
                </div>

                {/* TikTok */}
                <div>
                  <label className="block text-sm mb-1">TikTok</label>
                  {isEditing ? (
                    <Input
                      type="url"
                      placeholder="Enter TikTok URL"
                      value={footerData.tiktok_link}
                      onChange={(e) =>
                        handleFieldChange("tiktok_link", e.target.value)
                      }
                    />
                  ) : (
                    <p className="p-2 border rounded-md bg-muted/20">
                      {footerData.tiktok_link || "No TikTok link provided"}
                    </p>
                  )}
                </div>
              </div>
            </div>

            {/* Footer Booking Message */}
            <div className="border rounded-lg p-6">
              <h2 className="text-lg font-medium mb-4">
                Footer Booking Message
              </h2>
              <div className="space-y-4">
                <div>
                  {isEditing ? (
                    <Textarea
                      placeholder="Enter booking message"
                      value={footerData.text}
                      onChange={(e) =>
                        handleFieldChange("text", e.target.value)
                      }
                      rows={3}
                    />
                  ) : (
                    <p className="p-2 border rounded-md bg-muted/20">
                      {footerData.text || "No booking message provided"}
                    </p>
                  )}
                </div>
              </div>
            </div>

            {/* Save Button */}
            {isEditing && (
              <div className="flex justify-center">
                <Button
                  onClick={saveChanges}
                  disabled={isSaving}
                  className="bg-[#B87775] hover:bg-[#a56967] text-white px-8 py-2 uppercase tracking-wider"
                >
                  {isSaving ? "Saving..." : "Save Changes to Footer"}
                </Button>
              </div>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
}
